import { TaskLocation } from '@/api';
import { useEffect, useRef, useState } from 'react';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { preprocessCoord } from '../util/preprocess-coord';

/**
 * The what3words address a task location falls in.
 *
 * The coordinates are rounded before the lookup, so a device drifting by a
 * metre does not send us round again.
 */
export function useW3w(location: TaskLocation): {
    status: 'loading' | 'success' | 'error';
    data: string | null;
} {
    // The client is swapped out on token refresh, so never capture it: read
    // `openApiRef.current` at call time or you keep using a dead token.
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const [status, setStatus] = useState<'loading' | 'success' | 'error'>(
        'success'
    );
    const [data, setData] = useState<string | null>(null);

    const [latitude, setLatitude] = useState(preprocessCoord(location.lat));
    const [longitude, setLongitude] = useState(preprocessCoord(location.lng));

    useEffect(() => {
        setLatitude(preprocessCoord(location.lat));
        setLongitude(preprocessCoord(location.lng));
    }, [location]);

    useEffect(() => {
        if (!latitude || !longitude) {
            setData(null);
            setStatus('success');
            return;
        }

        const lng = longitude;
        const lat = latitude;

        setStatus('loading');
        openApiRef.current.w3W
            .getW3W({ lat, lng })
            .then((res) => {
                setData(res.data.data.words);
                setStatus('success');
            })
            .catch(() => {
                setStatus('error');
            });
    }, [latitude, longitude]);

    return { status, data };
}
